import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  OnInit,
  Optional,
  Output,
  ViewChild
} from '@angular/core';
import { ControlContainer, ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR, } from "@angular/forms";
import { SelectOption } from "../../core/models/select-option.model";
import { PatientService } from "../../core/services/patient.service";
import { debounceTime } from "rxjs/internal/operators/debounceTime";

@Component({
  selector: 'app-patient-dropdown',
  templateUrl: './patient-dropdown.component.html',
  styleUrl: './patient-dropdown.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PatientDropdownComponent),
      multi: true,
    },
  ],
})
export class PatientDropdownComponent implements ControlValueAccessor, OnInit, AfterViewInit {
  protected _patientService = inject(PatientService);

  @ViewChild('searchTermInput') input: ElementRef;

  @Output() cleared = new EventEmitter();

  @Input() apiConsumerIds: string[] = [];
  @Input() globalPatientsOnly = false;
  @Input() trialId: string = null;
  @Input() formControlName!: string;
  @Input() placeholder: string = 'Select patient...';
  @Input() truncate: number = 35;

  protected filterForm = new FormGroup({
    searchTerm: new FormControl<string>('')
  });
  protected value: any;
  protected dropdownIsVisible = false;
  protected selectedOption: SelectOption;
  protected searchHasValue = false;
  protected formGroup: FormGroup;
  protected formControl: FormControl;
  protected filteredPatients: SelectOption[] = [];
  protected isLoading = false;
  protected minSearchTermCharacters: number = 3;
  protected initialised = false;
  protected displayValue: string = '';

  constructor(@Optional() private controlContainer: ControlContainer) {

  }

  ngOnInit(): void {
    this.filterForm.get('searchTerm').valueChanges.subscribe(value => {
      this.searchHasValue = value !== '';
    });
  }

  ngAfterViewInit(): void {
    // Get the form control and the parent form group
    this.formGroup = this.controlContainer?.control as FormGroup | null;
    this.formControl = this.formGroup.get(this.formControlName) as FormControl | null;

    // Listen to the search term input for changes, and
    this.filterForm.get('searchTerm')?.valueChanges
      .pipe(debounceTime(300))
      .subscribe((value: string) => {
        if (value && value.length >= this.minSearchTermCharacters) {
          this.filter(value);
        } else if (value && value === '') {
          this.filteredPatients = [];
        }
      });

    // Listens for changes to the value of the main form control. If its the first change, i.e. an initial value
    // it will try to search for the patient and preselect that patient
    this.formControl.valueChanges.subscribe(value => {
      if (!this.initialised) {
        if (value && value !== '') {
          // Load patient and preselect it
          this.filter(value, true);
        }

        this.initialised = true;
      }
    });
  }

  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(value: any): void {
    this.value = value;
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  initWithPatientId(patientId: string) {
    this.filter(patientId, true);
    this.initialised = true;
  }

  protected onSelectPatient(option: SelectOption): void {
    this.selectedOption = option;
    this.value = option.value;
    this.displayValue = option.text.substring(0, this.truncate) + (option.text.length > this.truncate ? '...' : '');
    this.onChange(this.value);
    this.onTouched();
  }

  protected onOpenDropdown(): void {
    if (!this.formControl.disabled) {
      this.dropdownIsVisible = !this.dropdownIsVisible;

      if (this.dropdownIsVisible) {
        setTimeout(() => {
          this.input?.nativeElement.focus();
        }, 500);
      }
    }
  }

  /**
   * Clears the selected trial
   */
  protected onClearSelection(closeDropdown = false): void {
    if (this.formControl.disabled) {
      return;
    }

    this.selectedOption = undefined;
    this.filterForm.reset();
    this.formControl.reset();
    this.cleared.emit();

    if (closeDropdown) {
      setTimeout(() => {
        this.dropdownIsVisible = false
      }, 100);
    }
  }

  protected filter(val: string, selectIfSingleResult: boolean = false) {
    this.filteredPatients = [];
    this.isLoading = true;
    this._patientService.suggestPatient(val, this.trialId, this.globalPatientsOnly, this.apiConsumerIds).subscribe((patients: any) => {
      patients.forEach(patient => {
        this.filteredPatients.push(new SelectOption(patient.value, patient.text));
      });

      if (selectIfSingleResult && this.filteredPatients.length === 1) {
        this.onSelectPatient(this.filteredPatients[0]);
      }

      this.isLoading = false;
    });
  }
}
